import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Tag, Button } from '@/components/atoms'
import { SDDModal, DeployModal } from '@/components/modals'
import { FileTree } from '@/features/ide/components/FileTree'
import { CenterPane } from '@/features/ide/components/CenterPane'
import { StudioStage } from '@/features/ide/components/StudioStage'
import { WorkingIndicator } from '@/features/ide/components/WorkingIndicator'
import { ResultCards } from '@/features/ide/components/ResultCards'
import { LearningSlides } from '@/features/ide/components/LearningSlides'
import {
  VerificationPanel,
  type VerificationReceipt,
} from '@/features/ide/components/VerificationPanel'
import { useIdeStore } from '@/features/ide/store'
import { hashProjectFiles } from '@/features/ide/lib/project-hash'
import { createDeploymentTargetKey, isProjectQueryReady } from '@/features/ide/lib/run-identity'
import { useProjectHydrate } from '@/features/ide/hooks/useProjectHydrate'
import { useChatHydrate } from '@/features/ide/hooks/useChatHydrate'
import { useStarterPersistence } from '@/features/ide/hooks/useStarterPersistence'
import { useAgentRun } from '@/features/ide/hooks/useAgentRun'
import { apiFetch } from '@/lib/api'
import { cn } from '@/lib/utils'
import { MobileIdeNotice } from '@/components/MobileIdeNotice'
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport'
import { useAuthStore } from '@/stores/auth-store'

interface IdeSearch {
  project?: string
}

interface PlanDoc {
  successCriteria?: string[]
}

export const Route = createFileRoute('/ide')({
  component: IdePage,
  validateSearch: (search: Record<string, unknown>): IdeSearch => ({
    project: typeof search.project === 'string' ? search.project : undefined,
  }),
})

type OpenModal = 'sdd' | 'deploy' | null

const LEVEL_LABELS: Record<'starter' | 'intermediate' | 'advanced', string> = {
  starter: '입문',
  intermediate: '중급',
  advanced: '고급',
}

function IdePage() {
  // U-18 (spec §10.5) — 모바일(767px 미만)은 IDE 진입 차단 + 안내 화면.
  // hook 순서 보존을 위해 wrapper 에서 분기 (IdeWorkspace 는 데스크탑에서만 mount).
  // 단, 한 번 작업실에 들어온 뒤에는 리사이즈로 unmount 하지 않는다 —
  // unmount 시 useAgentRun cleanup 이 진행 중 agent run 을 abort 하기 때문 (진입 시점만 차단).
  const isMobile = useIsMobileViewport()
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    if (!isMobile) setEntered(true)
  }, [isMobile])
  if (isMobile && !entered) return <MobileIdeNotice />
  return <IdeWorkspace />
}

function IdeWorkspace() {
  const { project: projectIdFromUrl } = useSearch({ from: '/ide' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const projectHydrate = useProjectHydrate(projectIdFromUrl)
  const idToken = useAuthStore((s) => s.idToken)
  const projectQueriesAuthReady = isProjectQueryReady(
    projectIdFromUrl,
    idToken,
    import.meta.env.DEV,
  )
  useChatHydrate(projectIdFromUrl)
  // 커밋 전 starter 작업을 localStorage 로 보존 — 하드 리로드에도 사라지지 않게.
  useStarterPersistence(projectIdFromUrl)

  const [open, setOpen] = useState<OpenModal>(null)
  const close = () => setOpen(null)

  const projectId = useIdeStore((s) => s.projectId)
  const projectTitle = useIdeStore((s) => s.title)
  const projectLevel = useIdeStore((s) => s.level)
  const phase = useIdeStore((s) => s.phase)
  const device = useIdeStore((s) => s.device)
  const setDevice = useIdeStore((s) => s.setDevice)
  const learned = useIdeStore((s) => s.learnedToolUse)
  const markLearned = useIdeStore((s) => s.markLearnedToolUse)
  const applyPatch = useIdeStore((s) => s.applyPatch)
  const agentRun = useIdeStore((s) => s.agentRun)
  const files = useIdeStore((s) => s.files)
  const build = useIdeStore((s) => s.build)
  const activeProjectIdRef = useRef(projectIdFromUrl)
  activeProjectIdRef.current = projectIdFromUrl
  const [sourceDigest, setSourceDigest] = useState<{
    files: typeof files
    hash: string
  } | null>(null)
  const sourceHash = sourceDigest?.files === files ? sourceDigest.hash : null

  useEffect(() => {
    let cancelled = false
    if (Object.keys(files).length === 0) {
      setSourceDigest(null)
      return
    }
    void hashProjectFiles(files).then((hash) => {
      if (!cancelled) setSourceDigest({ files, hash })
    })
    return () => {
      cancelled = true
    }
  }, [files])

  // spec §11.D R-M03 — 검증 receipt와 현재 plan 성공 기준을 함께 조회한다. 소스뿐 아니라
  // 성공 기준이 바뀐 경우에도 예전 receipt로 배포할 수 없다.
  const { data: receipt } = useQuery({
    queryKey: ['project-receipt', projectIdFromUrl, projectQueriesAuthReady],
    queryFn: () =>
      apiFetch<VerificationReceipt | null>(`/api/projects/${projectIdFromUrl}/receipt`),
    enabled: projectQueriesAuthReady,
    retry: false,
  })
  const { data: planDoc } = useQuery({
    queryKey: ['project-plan', projectIdFromUrl, projectQueriesAuthReady],
    queryFn: () => apiFetch<PlanDoc>(`/api/plan/${projectIdFromUrl}`),
    enabled: projectQueriesAuthReady,
    retry: false,
  })
  const criteriaJson = JSON.stringify(planDoc?.successCriteria ?? [])
  const [criteriaDigest, setCriteriaDigest] = useState<{
    json: string
    hash: string
  } | null>(null)
  const criteriaHash = criteriaDigest?.json === criteriaJson ? criteriaDigest.hash : null

  useEffect(() => {
    let cancelled = false
    void hashText(criteriaJson).then((hash) => {
      if (!cancelled) setCriteriaDigest({ json: criteriaJson, hash })
    })
    return () => {
      cancelled = true
    }
  }, [criteriaJson])

  useEffect(() => {
    if (agentRun.status === 'done' && projectIdFromUrl) {
      void queryClient.invalidateQueries({ queryKey: ['project-receipt', projectIdFromUrl] })
    }
  }, [agentRun.status, projectIdFromUrl, queryClient])
  const receiptMatchesCurrentSource = Boolean(
    receipt?.schemaVersion === 3 &&
    receipt.runtimeAuthMode === 'host-proxy-v1' &&
    receipt?.buildOk === true &&
    receipt.previewOk === true &&
    receipt.successCriteriaOk === true &&
    receipt.sourceHash === sourceHash &&
    receipt.criteriaHash === criteriaHash &&
    receipt.criteriaTotal > 0 &&
    receipt.criteriaPassed === receipt.criteriaTotal &&
    receipt.buildEvidence?.kind === 'trusted-api-builder' &&
    receipt.buildEvidence.sourceHash === receipt.sourceHash &&
    receipt.buildEvidence.artifactHash === receipt.artifactHash &&
    receipt.previewEvidence?.kind === 'student-self-attestation' &&
    receipt.previewEvidence.sourceHash === receipt.sourceHash &&
    receipt.criteriaEvidence?.kind === 'student-self-attestation' &&
    receipt.criteriaEvidence.criteriaHash === receipt.criteriaHash &&
    receipt.criteriaEvidence.checkedCriteria.length === receipt.criteriaTotal,
  )
  const deploymentTargetKey = createDeploymentTargetKey(
    projectIdFromUrl,
    receiptMatchesCurrentSource && receipt ? receipt : null,
  )

  const [deployedRelease, setDeployedRelease] = useState<{
    targetKey: string
    url: string
    version: string
  } | null>(null)
  const deployedUrl = deployedRelease?.targetKey === deploymentTargetKey ? deployedRelease.url : ''
  const [isDeploying, setIsDeploying] = useState(false)
  const deploySequenceRef = useRef(0)
  const deployAbortRef = useRef<AbortController | null>(null)
  const activeDeployTargetRef = useRef(deploymentTargetKey)
  activeDeployTargetRef.current = deploymentTargetKey
  const deployBlockedReason = (() => {
    if (phase !== 'idle' || agentRun.status === 'running')
      return '작품 만들기가 끝나면 점검할 수 있어요.'
    if (!sourceHash) return '작품 파일을 불러오는 중이에요.'
    if (build.status === 'pending') return '미리보기 빌드가 끝나길 기다리고 있어요.'
    if (build.status === 'error') return '미리보기 오류를 먼저 해결해 주세요.'
    if (!planDoc) return '작품의 성공 기준을 불러오는 중이에요.'
    if ((planDoc.successCriteria?.length ?? 0) === 0)
      return '계획에 성공 기준이 없어 점검할 수 없어요.'
    if (!criteriaHash) return '성공 기준을 확인하는 중이에요.'
    if (!receipt) return '위의 작품 점검을 완료하면 공개할 수 있어요.'
    if (receipt.sourceHash !== sourceHash) return '수정된 작품을 다시 점검해 주세요.'
    if (receipt.criteriaHash !== criteriaHash) return '바뀐 성공 기준으로 다시 점검해 주세요.'
    if (!receiptMatchesCurrentSource) return '빌드·미리보기·성공 기준 점검을 다시 완료해 주세요.'
    return null
  })()
  const canDeploy = deployBlockedReason === null

  // project 또는 검증된 receipt/artifact가 바뀌면 느린 이전 배포를 취소한다.
  // 이전 URL은 identity와 같이 보관하되 현재 target과 다르면 재사용하지 않는다.
  useEffect(() => {
    deploySequenceRef.current += 1
    deployAbortRef.current?.abort()
    deployAbortRef.current = null
    setIsDeploying(false)
    setOpen(null)
    return () => {
      deploySequenceRef.current += 1
      const controller = deployAbortRef.current
      controller?.abort()
      if (deployAbortRef.current === controller) deployAbortRef.current = null
    }
  }, [deploymentTargetKey, projectIdFromUrl])

  // Verify 단계에서 로컬 esbuild를 확인한 뒤, 서버가 R2 원본으로 직접 만든 dist만 공개한다.
  const runDeploy = async (deployTargetKey: string) => {
    const deployProjectId = projectIdFromUrl
    if (!deployProjectId) return
    const sequence = ++deploySequenceRef.current
    deployAbortRef.current?.abort()
    const controller = new AbortController()
    deployAbortRef.current = controller
    setIsDeploying(true)
    try {
      const data = await apiFetch<{ url: string; version: string }>('/api/deploy', {
        method: 'POST',
        body: JSON.stringify({ projectId: deployProjectId }),
        signal: controller.signal,
      })
      if (
        controller.signal.aborted ||
        activeProjectIdRef.current !== deployProjectId ||
        activeDeployTargetRef.current !== deployTargetKey
      )
        return
      setDeployedRelease({ targetKey: deployTargetKey, url: data.url, version: data.version })
      toast.success('작품이 공개됐어요!', { description: '링크를 친구한테 보내볼까요?' })
    } catch (err) {
      if (
        controller.signal.aborted ||
        activeProjectIdRef.current !== deployProjectId ||
        activeDeployTargetRef.current !== deployTargetKey
      )
        return
      setOpen(null)
      toast.error(
        err instanceof Error ? err.message : '배포하지 못했어요. 잠시 후 다시 시도해 주세요.',
      )
    } finally {
      if (deploySequenceRef.current === sequence) {
        deployAbortRef.current = null
        setIsDeploying(false)
      }
    }
  }

  const handleDeployClick = () => {
    if (!canDeploy || !deploymentTargetKey) {
      // 서버 403 RECEIPT_REQUIRED 메시지와 톤 통일.
      toast('작품 점검을 통과하면 배포할 수 있어요', {
        description: deployBlockedReason ?? '작업실에서 점검을 다시 완료해 주세요.',
      })
      return
    }
    setOpen('deploy')
    // 같은 receipt+artifact만 URL을 재사용한다. 새 receipt는 공개 URL이 같아도 다시 POST한다.
    if (deployedUrl || isDeploying || deployAbortRef.current) return
    void runDeploy(deploymentTargetKey)
  }

  // plan v1.3 G2 — IDE 진입 화이트리스트. building/coding/idle 만 허용, 그 외 모두 /plan redirect.
  // 'review' phase (multi-agent 검토) 와 'cards' (UI상 단계, backend phase 는 'interview' 인 동안) 모두 차단.
  // dev 모드는 ?dev=1 query 로 우회 가능 (SDDModal 데모 흐름 보존).
  useEffect(() => {
    if (!projectIdFromUrl) return
    if (phase === null) return // hydrate 전 — flicker 방지. 실패 시 재시도 안내를 표시한다.
    const isDev = import.meta.env.DEV && new URLSearchParams(window.location.search).has('dev')
    if (isDev) return
    if (phase !== 'building' && phase !== 'coding' && phase !== 'idle') {
      void navigate({
        to: '/plan',
        search: { project: projectIdFromUrl, phase: 'interview' },
        replace: true,
      })
    }
  }, [phase, projectIdFromUrl, navigate])

  // agent loop 자동 시작 — phase='building' + agentRun.status='idle' 일 때만 트리거.
  // ref guard 대신 status 자체로 중복 트리거 방지. start() 가 즉시 'running' 으로 바꾸므로
  // StrictMode 두 번째 effect 시 condition 자동 falsy. phase 재진입(idle 로 reset 후) 도 정상 재시작.
  const { start: startAgent, stop: stopAgent } = useAgentRun(projectIdFromUrl)
  useEffect(() => {
    if (phase !== 'building') return
    if (agentRun.status !== 'idle') return
    void startAgent()
  }, [phase, agentRun.status, startAgent])

  const handleAgentRetry = () => {
    const current = useIdeStore.getState()
    if (current.agentRun.status !== 'error' && current.agentRun.status !== 'stopped') return

    // terminal event가 useAgentRun loop를 끝낸 뒤에만 버튼이 렌더된다. 한 event handler
    // 안에서 reset과 start를 연달아 실행하면 React effect가 중간 idle을 관찰하지 못해
    // building+idle 자동 시작과 명시적 재시작이 중복되지 않는다.
    current.resetAgentRun()
    void startAgent()
  }

  // stop 버튼 advisory — plan §15 rule 11 "끊지마" 학습 안내.
  // 토스트 actions 로 "그래도 멈출게요" 누르면 그때 진짜 abort.
  const handleStopRequest = () => {
    toast(
      '잘 짜인 plan 대로 일할 땐 끝까지 보고, 마음에 안 들면 그때 자연어로 수정 요청하는 게 효율적이에요.',
      {
        description: '끊으면 쓰다 만 코드 + 오류가 누적될 수 있어요.',
        duration: 8000,
        action: {
          label: '그래도 멈출게요',
          onClick: () => stopAgent(),
        },
      },
    )
  }

  return (
    <div className="bg-stage relative grid h-[calc(100vh-56px)] grid-cols-[240px_minmax(0,1fr)_minmax(0,1fr)] gap-0">
      {projectHydrate.status === 'error' && (
        <div className="bg-stage/95 absolute inset-0 z-50 grid place-items-center p-6" role="alert">
          <div className="border-border bg-surface shadow-card w-full max-w-sm rounded-2xl border p-6 text-center">
            <h2 className="text-text text-lg font-bold">프로젝트를 불러오지 못했어요</h2>
            <p className="text-text-2 mt-2 text-sm leading-6">
              {projectHydrate.error ?? '인터넷 연결을 확인한 뒤 다시 시도해 주세요.'}
            </p>
            <Button className="mt-5" onClick={projectHydrate.retry}>
              다시 불러오기
            </Button>
          </div>
        </div>
      )}
      <aside className="border-border bg-surface overflow-y-auto border-r p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-text truncate text-[13px] font-semibold" title={projectTitle}>
            {projectTitle || '제목 없는 프로젝트'}
          </p>
          <Tag variant="primary">{LEVEL_LABELS[projectLevel]}</Tag>
        </div>
        <p className="text-text-3 mb-3 truncate font-mono text-[11px]" title={projectId}>
          {projectId}
        </p>

        <FileTree />

        {agentRun.status !== 'idle' && (
          <div className="mt-4">
            <WorkingIndicator onStop={handleStopRequest} onRetry={handleAgentRetry} />
          </div>
        )}

        {/* 학습 슬라이드 — agent loop 진행 중 자동 재생 (plan §10 PR #13) */}
        <div className="mt-4">
          <LearningSlides />
        </div>

        {phase === 'idle' && projectIdFromUrl && agentRun.status !== 'running' && (
          <div className="mt-4">
            <VerificationPanel
              projectId={projectIdFromUrl}
              sourceHash={sourceHash}
              alreadyVerified={receiptMatchesCurrentSource}
            />
          </div>
        )}

        {/* 결과 카드뉴스 — agent 가 <CARD_START> 로 출력한 카드들 (plan §9.1 + §11.A)
            tool_learning 카드가 섞여 있으면 학생이 끝까지 본 시점에 learnedToolUse Firestore patch
            + store 갱신 (plan §14.6 PR #7). */}
        {(agentRun.status === 'done' || agentRun.status === 'stopped') &&
          agentRun.resultCards.length > 0 && (
            <div className="mt-4">
              <ResultCards
                cards={agentRun.resultCards}
                canPublish={receiptMatchesCurrentSource}
                onDismiss={async () => {
                  const hasToolLearning = agentRun.resultCards.some(
                    (c) => c.kind === 'tool_learning',
                  )
                  if (!hasToolLearning || learned) return
                  // 더블 클릭 방어 — markLearned() 를 await 전에 호출해 다음 클릭에서 learned===true 로
                  // 가드 통과. fsWrite merge 는 멱등이지만 토스트 중복은 UX 손상.
                  markLearned()
                  try {
                    await apiFetch('/api/auth/learned-tool-use', { method: 'POST' })
                    toast.success('이제 외부 데이터를 가져올 수 있어요')
                  } catch (err) {
                    console.warn('learnedToolUse_patch_failed', err)
                  }
                }}
              />
            </div>
          )}

        {import.meta.env.DEV && (
          <>
            <hr className="border-border my-5" />
            <p className="text-text-2 mb-2 text-[12px] font-semibold">데모 트리거 (DEV)</p>
            <div className="flex flex-col gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setOpen('sdd')}>
                먼저 정리하기 열기
              </Button>
            </div>
          </>
        )}

        <hr className="border-border my-5" />

        <p className="text-text-2 mb-2 text-[12px] font-semibold">미리보기 디바이스</p>
        <div className="grid grid-cols-3 gap-1.5">
          {(['mobile', 'tablet', 'desktop'] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDevice(d)}
              className={cn(
                'h-7 rounded-md text-[11px] font-semibold transition-colors',
                device === d
                  ? 'bg-primary-soft text-primary'
                  : 'text-text-2 hover:bg-surface-alt border-border border',
              )}
            >
              {d === 'mobile' ? '📱' : d === 'tablet' ? '💻' : '🖥'}
              <span className="ml-1">
                {d === 'mobile' ? '모바일' : d === 'tablet' ? '태블릿' : '데스크탑'}
              </span>
            </button>
          ))}
        </div>

        <hr className="border-border my-5" />

        {/* spec §11.D — 검증 receipt(buildOk) 통과 전에는 클릭 시 안내 토스트만. */}
        <Button
          block
          onClick={handleDeployClick}
          disabled={!canDeploy || isDeploying}
          className={cn(!canDeploy && 'opacity-50')}
          title={canDeploy ? '내 작품을 링크로 공개해요' : (deployBlockedReason ?? undefined)}
          aria-describedby={!canDeploy ? 'deploy-help' : undefined}
        >
          {isDeploying ? '공개하는 중…' : '작품 공개하기'}
        </Button>
        {!canDeploy && deployBlockedReason && (
          <p id="deploy-help" className="text-text-3 mt-2 text-[11px] leading-4" role="status">
            {deployBlockedReason}
          </p>
        )}
      </aside>

      <CenterPane />

      <StudioStage />

      <SDDModal
        open={open === 'sdd'}
        onOpenChange={(o) => (o ? setOpen('sdd') : close())}
        draft={
          import.meta.env.DEV
            ? {
                goal: '오늘 할 일을 추가하고 ✓ 체크할 수 있는 투두 앱',
                io: '입력: 새 할 일 텍스트 / 출력: 목록과 진행률(%)',
                steps: '1) 입력창 + 버튼\n2) 리스트 렌더\n3) 체크 토글\n4) 진행률 계산',
                done: '할 일 5개 넣고 3개 체크하면 진행률이 60%로 보인다',
              }
            : {}
        }
        onConfirm={(final) => {
          toast.success('정리 끝! 만들기 시작할게요.', { description: final.goal })
          if (!import.meta.env.DEV) return
          // DEV 데모: 명세 승인 시 투두 앱 샘플 코드를 패치 (실 운영은 백엔드 SSE 연결)
          applyPatch({
            '/src/App.tsx': `import { useState } from 'react'

interface Todo { id: number; text: string; done: boolean }

export default function App() {
  const [items, setItems] = useState<Todo[]>([])
  const [draft, setDraft] = useState('')
  const progress = items.length === 0 ? 0 : Math.round(items.filter(t => t.done).length / items.length * 100)

  const add = () => {
    if (!draft.trim()) return
    setItems([...items, { id: Date.now(), text: draft, done: false }])
    setDraft('')
  }
  const toggle = (id: number) =>
    setItems(items.map(t => t.id === id ? { ...t, done: !t.done } : t))

  return (
    <main style={{ padding: 32, fontFamily: 'sans-serif', maxWidth: 480, margin: '0 auto' }}>
      <h1 style={{ color: '#3182F6' }}>오늘의 할 일</h1>
      <p style={{ color: '#4E5968' }}>진행률: {progress}%</p>
      <div style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="할 일을 적어보세요"
          style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #E5E8EB' }}
        />
        <button onClick={add} style={{ padding: '10px 16px', borderRadius: 8, background: '#3182F6', color: '#fff', border: 0 }}>추가</button>
      </div>
      <ul style={{ padding: 0, listStyle: 'none' }}>
        {items.map(t => (
          <li key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
            <input type="checkbox" checked={t.done} onChange={() => toggle(t.id)} />
            <span style={{ textDecoration: t.done ? 'line-through' : 'none', color: t.done ? '#8B95A1' : '#191F28' }}>{t.text}</span>
          </li>
        ))}
      </ul>
    </main>
  )
}
`,
          })
        }}
        onRegenerate={() => toast.info('초안을 다시 받아오는 중이에요')}
      />
      <DeployModal
        open={open === 'deploy'}
        onOpenChange={(o) => (o ? setOpen('deploy') : close())}
        url={deployedUrl}
        onShareKakao={() => toast.info('카카오톡 공유는 준비 중이에요')}
        onPublishToArchive={() => toast.success('친구들 작품에 올렸어요!')}
      />
    </div>
  )
}

async function hashText(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}
